import useAppFeedbackRepo from '@/composables/app-feedback/app-feedback-repo';
import useDataSync from '@/composables/subscription/data-sync';
import useFeedEntryRepo from '@/composables/feed-entry/feed-entry-repo';
import useGoalActivityDataSync from '@/composables/goal-activity/goal-activity-data-sync';
import useGoalSettings from '@/composables/logged-in-user-account/goal-settings';
import useGridPageRepo from '@/composables/grid-page/grid-page-repo';
import useGridPageSubscription from '@/composables/grid-page/grid-page-subscription';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useRepo from '@/nebula/repo';
import { EVENTS } from '@/lib/constants';
import { EventBus } from '@/lib/event-bus';
import {
  accessGroup as accessGroupConfig,
  accessPolicy as accessPolicyConfig,
  accessPolicyScope as accessPolicyScopeConfig,
  account as accountConfig,
  accountSettings as accountSettingsConfig,
  appFeedback as appFeedbackConfig,
  appIntegration as appIntegrationConfig,
  asanaQuery as asanaQueryConfig,
  comment as commentConfig,
  companyInfo as companyInfoConfig,
  customerContract as customerContractConfig,
  dataSource as dataSourceConfig,
  favorite as favoriteConfig,
  feedEntry as feedEntryConfig,
  goalActivity as goalActivityConfig,
  goal as goalConfig,
  goalCycle as goalCycleConfig,
  goalSettings as goalSettingsConfig,
  goalSubscription as goalSubscriptionConfig,
  hubspotQuery as hubspotQueryConfig,
  invitation as invitationConfig,
  jiraQuery as jiraQueryConfig,
  notificationSetting as notificationSettingConfig,
  oauthCodeGrantClient as oauthCodeGrantClientConfig,
  personalAppSettings as personalAppSettingsConfig,
  pin as pinConfig,
  property as propertyConfig,
  propertyOption as propertyOptionConfig,
  propertySettings as propertySettingsConfig,
  propertyValue as propertyValueConfig,
  reaction as reactionConfig,
  salesforceQuery as salesforceQueryConfig,
  saml as samlConfig,
  savedView as savedViewConfig,
  selectedView as selectedViewConfig,
  space as spaceConfig,
  spreadsheetCell as spreadsheetCellConfig,
  storageObject as storageObjectConfig,
  update as updateConfig,
  user as userConfig,
  userProvisioning as userProvisioningConfig,
  userProvisioningMapping as userProvisioningMappingConfig,
  userScope as userScopeConfig,
  userScopeTree as userScopeTreeConfig,
} from 'shared/api/query/configs.json';
import { ref } from 'vue';
import { routeName } from 'shared/constants.json';
import { showOnboarding } from '@/lib/onboarding/onboarding';
import { useRoute, useRouter } from 'vue-router';

export default function useLogin(pageVisitsService) {
  const route = useRoute();
  const router = useRouter();

  const loading = ref(true);

  const { loggedInUser, getLoggedInUser } = useLoggedInUser();
  const { loggedInUserAccount } = useLoggedInUserAccount();

  const findLastPage = () => {
    const page = pageVisitsService.getLastPageVisit();
    if (page === null) {
      return { ok: false };
    }

    if (page.routeName === routeName.logout) {
      return { ok: false };
    }

    if (!routeName.all.includes(page.routeName)) {
      return { ok: false };
    }

    return { ok: true, page };
  };

  const loggedInUserResponseHandler = (async () => {
    EventBus.$emit(EVENTS.SESSION.LOGIN, { user: loggedInUser.value, account: loggedInUserAccount.value });

    pageVisitsService.fetchPageVisits({ user: loggedInUser.value });

    if (typeof route.meta.title !== 'undefined') {
      document.title = `${route.meta.title()} | Mooncamp`;
    }

    if (showOnboarding({ user: loggedInUser.value, account: loggedInUserAccount.value })) {
      await router.replace({ name: routeName.onboarding });
      return;
    }

    if (route.name !== routeName.home || Object.keys(route.query).length > 0) {
      return;
    }

    const { page, ok } = findLastPage();
    if (ok) {
      const params = page.params;
      const query = page.query;
      await router.replace({ name: page.routeName, params, query });
    }
  });

  const gridPagesRepo = useGridPageRepo();
  const gridPagesSubscriptionSvc = useGridPageSubscription();

  const goalSettingsSvc = useGoalSettings();

  const repos = {};
  const subscribers = {};
  const configs = [
    { model: favoriteConfig.model, fetch: true, subscribe: true },
    { model: goalCycleConfig.model, fetch: true, subscribe: true },
    { model: propertyOptionConfig.model, fetch: true, subscribe: true },
    { model: propertyConfig.model, fetch: true, subscribe: true },
    { model: propertySettingsConfig.model, fetch: true, subscribe: true },
    { model: spaceConfig.model, fetch: true, subscribe: true },
    { model: userConfig.model, fetch: true, subscribe: true },
    { model: invitationConfig.model, fetch: false, subscribe: true },
    { model: propertyValueConfig.model, fetch: false, subscribe: true, syncOptions: { ignoreNonExistingData: true } },
    { model: personalAppSettingsConfig.model, fetch: false, subscribe: true },
    { model: pinConfig.model, fetch: true, subscribe: true },
    { model: notificationSettingConfig.model, fetch: false, subscribe: true },
    { model: storageObjectConfig.model, fetch: false, subscribe: true, syncOptions: { ignoreNonExistingData: true } },
    { model: accessGroupConfig.model, fetch: true, subscribe: true },
    { model: accessPolicyConfig.model, fetch: false, subscribe: true, syncOptions: { ignoreNonExistingData: true } },
    { model: accessPolicyScopeConfig.model, fetch: false, subscribe: true, syncOptions: { ignoreNonExistingData: true } },
    { model: userScopeTreeConfig.model, fetch: false, subscribe: true, syncOptions: { ignoreNonExistingData: true } },
    { model: userScopeConfig.model, fetch: false, subscribe: true, syncOptions: { ignoreNonExistingData: true } },
    { model: accountConfig.model, fetch: true, subscribe: true },
    { model: accountSettingsConfig.model, fetch: false, subscribe: true },
    { model: goalSettingsConfig.model, fetch: false, subscribe: true },
    { model: appIntegrationConfig.model, fetch: false, subscribe: true },
    { model: customerContractConfig.model, fetch: false, subscribe: true },
    { model: companyInfoConfig.model, fetch: false, subscribe: true },
    { model: samlConfig.model, fetch: false, subscribe: true },
    { model: userProvisioningConfig.model, fetch: false, subscribe: true },
    { model: userProvisioningMappingConfig.model, fetch: false, subscribe: true },
    { model: oauthCodeGrantClientConfig.model, fetch: false, subscribe: true },
    { model: commentConfig.model, fetch: false, subscribe: true, syncOptions: { ignoreNonExistingData: true } },
    { model: reactionConfig.model, fetch: false, subscribe: true, syncOptions: { ignoreNonExistingData: true } },
    { model: updateConfig.model, fetch: false, subscribe: true, syncOptions: { ignoreNonExistingData: true } },
    { model: goalActivityConfig.model, fetch: false, subscribe: true, subscriber: useGoalActivityDataSync, syncOptions: { ignoreNonExistingData: true } },
    { model: appFeedbackConfig.model, fetch: true, subscribe: false, repo: useAppFeedbackRepo },
    { model: selectedViewConfig.model, fetch: true, subscribe: true },
    { model: savedViewConfig.model, fetch: true, subscribe: true },
    { model: feedEntryConfig.model, fetch: true, subscribe: true, repo: useFeedEntryRepo, syncOptions: { ignoreNonExistingData: true } },
    { model: dataSourceConfig.model, fetch: true, subscribe: true },
    { model: goalConfig.model, fetch: false, subscribe: true, syncOptions: { ignoreNonExistingData: true } },
    { model: goalSubscriptionConfig.model, fetch: false, subscribe: true, syncOptions: { ignoreNonExistingData: true } },
    { model: asanaQueryConfig.model, fetch: false, subscribe: true, syncOptions: { ignoreNonExistingData: true } },
    { model: hubspotQueryConfig.model, fetch: false, subscribe: true, syncOptions: { ignoreNonExistingData: true } },
    { model: jiraQueryConfig.model, fetch: false, subscribe: true, syncOptions: { ignoreNonExistingData: true } },
    { model: salesforceQueryConfig.model, fetch: false, subscribe: true, syncOptions: { ignoreNonExistingData: true } },
    { model: spreadsheetCellConfig.model, fetch: false, subscribe: true, syncOptions: { ignoreNonExistingData: true } },
  ];

  configs.forEach((config) => {
    if (config.repo !== undefined) {
      repos[config.model] = config.repo();
    } else {
      repos[config.model] = useRepo(config.model);
    }
    if (config.subscriber !== undefined) {
      subscribers[config.model] = config.subscriber(repos[config.model], config.syncOptions);
    } else {
      subscribers[config.model] = useDataSync(repos[config.model], config.syncOptions);
    }
  });

  const initializeUser = () => {
    loading.value = true;
    const promises = [];
    promises.push(getLoggedInUser().then((data) => data).catch(() => null));
    promises.push(gridPagesRepo.queryGridPages().catch(() => {}));
    configs.forEach((config) => {
      if (!config.fetch) {
        return;
      }
      promises.push(repos[config.model].getList().catch(() => {}));
    });
    return Promise.all(promises).then(async (responses) => {
      if (responses[0] !== null) {
        await loggedInUserResponseHandler();
        // subscriptions can only start from here
        gridPagesSubscriptionSvc.subscribe();
        goalSettingsSvc.setDefaultValueFeatureName();
        configs.forEach((config) => {
          if (!config.subscribe) {
            return;
          }

          const goalCollabAccounts = [260015, 77349129, 2845991, 56467436, 30305333, 26749438, 51371754, 76714102, 54457529, 12974043, 73607479];
          // TODO: remove this is a quick A/B for rolling out goal collab
          if ([goalConfig.model].includes(config.model) && !goalCollabAccounts.includes(loggedInUserAccount.value.uid)) {
            return;
          }
          subscribers[config.model].subscribe();
        });
      }
      return responses[0];
    }).finally(() => {
      loading.value = false;
    });
  };

  return { loading, initializeUser };
}
